import validator from 'validator';
import * as yup from 'yup';

// 半角英数字(大文字, 小文字, 数字)それぞれを含む8文字以上50文字以下の正規表現
const passwordRegex = new RegExp(
  /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[0-9a-zA-Z!-/:-@[-`{-~\]]{8,50}$/
);

export const isValidPassword = (password?: string): boolean =>
  passwordRegex.test(password ?? '');

export const isValidPasswordLength = (password: string): boolean =>
  password.length >= 8 && password.length <= 50;

export const isValidPasswordNumeric = (password: string): boolean =>
  /[0-9]/.test(password);

export const isValidPasswordUppercaseAlphabet = (password: string): boolean =>
  /[A-Z]/.test(password);

export const isValidPasswordLowercaseAlphabet = (password: string): boolean =>
  /[a-z]/.test(password);

/**
 * Emailのバリデーション
 * @param email メールアドレス
 */
export const isValidEmail = (email?: string) => {
  if (typeof email === 'undefined') return false;

  return !validator.isEmpty(email) && validator.isEmail(email ?? '');
};

/**
 * Emailのバリデーション結果のメッセージ
 * @param email
 */
export const emailValidateErrorMessages = (email?: string) => {
  return {
    requireMail: {
      hasError: validator.isEmpty(email ?? ''),
      message: 'メールアドレスが入力されていません。',
    },
    mailRegulation: {
      hasError:
        !validator.isEmpty(email ?? '') && !validator.isEmail(email ?? ''),
      message: 'メールアドレスの形式が正しくありません。',
    },
  };
};

/**
 * Passwordのバリデーション結果のメッセージ
 * @param password
 */
export const passwordValidateErrorMessages = (password?: string) => {
  return {
    requirePassword: {
      hasError: validator.isEmpty(password ?? ''),
      message: 'パスワードが入力されていません。',
    },
    passwordRegulation: {
      hasError:
        password !== null &&
        !validator.isEmpty(password ?? '') &&
        !isValidPassword(password ?? ''),
      message: '大文字・小文字・数字を含む8文字以上で入力してください。',
    },
  };
};

/**
 * YupでのValidateスキーマ
 */
export const creditCardNumberValidatorSchema = yup
  .string()
  .test(
    'paymentInformation.creditCardNumber',
    'カード番号の桁数は15桁もしくは16桁です',
    value => {
      if (typeof value !== 'string' || value.length === 0) return false;
      const wordCountWithoutSpace = value.replace(/\s/g, '').length;
      return wordCountWithoutSpace === 15 || wordCountWithoutSpace === 16;
    }
  )
  .required('カード番号は必須です');

export const firstNameValidatorSchema = yup
  .string()
  .matches(
    /^[A-Za-z0-9 \-.,_ⅠⅡⅢⅣⅤⅥⅦⅧⅨⅩⅪⅫⅰⅱⅲⅳⅴⅵⅶⅷⅸⅹⅺⅻ]*$/,
    '名で使える文字は英数字と記号(-_,.)のみです'
  )
  .required('名（ローマ字）は必須項目です');

export const lastNameValidatorSchema = yup
  .string()
  .matches(
    /^[A-Za-z0-9 \-.,_ⅠⅡⅢⅣⅤⅥⅦⅧⅨⅩⅪⅫⅰⅱⅲⅳⅴⅵⅶⅷⅸⅹⅺⅻ]*$/,
    '姓で使える文字は半角英数字と記号(-_,.)、半角スペースのみです'
  )
  .required('姓（ローマ字）は必須項目です');

export const mobilePhoneNumberValidatorSchema = yup
  .string()
  .required('携帯電話番号は必須項目です')
  .matches(/^(070|080|090)[0-9]{8}$/, '携帯電話番号を入力してください');

export const phoneNumberValidatorSchema = yup
  .string()
  .required('電話番号は必須項目です')
  .min(10, '電話番号を正しく入力してください')
  .max(11, '電話番号を正しく入力してください')
  .matches(/^[0-9]+$/, '電話番号を正しく入力してください');

export const emailValidatorSchema = yup
  .string()
  .required('メールアドレスが入力されていません。')
  .email('メールアドレスの形式が正しくありません。');

export const passwordValidatorSchema = yup
  .string()
  .required('パスワードが入力されていません')
  .matches(
    /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[0-9a-zA-Z!-/:-@[-`{-~\]]{8,50}$/,
    'パスワードは大文字・小文字・数字を含む8文字以上50文字以下で入力してください。'
  );

export const firstNameHiraganaValidatorSchema = yup
  .string()
  .matches(/^[ぁ-ん]*$/, '名はひらがなのみです')
  .required('名（ひらがな）は必須です');

export const lastNameHiraganaValidatorSchema = yup
  .string()
  .matches(/^[ぁ-ん]*$/, '姓はひらがなのみです')
  .required('姓（ひらがな）は必須です');
